import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { useNarrator } from "@/lib/narrator";
import { NARRATOR } from "@/constants/testIds";

/**
 * NarratedText — reverent block-level narration with a 1-second fade-in.
 *
 * Word-by-word sync has been removed in favour of a single utterance +
 * soft block fade — this hides the robotic artifacts of browser TTS.
 *
 * Modes:
 *   ▸ auto     (manual=false, autoSpeak=true, default)
 *              Starts hidden; auto-calls speak() on mount, then fades in.
 *   ▸ manual   (manual=true)
 *              Text starts fully visible. Renders a Soft-Gold Play/Pause
 *              pill. Narration only triggers on Play.
 *   ▸ external (manual=false, autoSpeak=false)
 *              Starts hidden; waits for the parent to invoke speak(id),
 *              then fades in.
 */
export default function NarratedText({
  text,
  id,
  as: Tag = "p",
  className = "",
  autoSpeak = true,
  manual = false,
  delay = 250,
  style = {},
}) {
  const {
    speak,
    stop,
    supported,
    muted,
    currentUtteranceId,
    isSpeaking,
  } = useNarrator();

  const active = currentUtteranceId === id;
  const isAutoSpeak = !manual && autoSpeak && supported && !muted;

  const [revealed, setRevealed] = useState(false);
  const startedRef = useRef(false);

  // Reset visibility whenever text/mode changes
  useEffect(() => {
    if (!supported || muted || manual) {
      setRevealed(true); // fully visible when narrator is off or manual
      return;
    }
    setRevealed(false);
    startedRef.current = false;
  }, [text, id, manual, supported, muted]);

  // Auto-play trigger
  useEffect(() => {
    if (!isAutoSpeak) return;
    const t = setTimeout(() => {
      startedRef.current = true;
      speak(text, id);
      setRevealed(true); // 1s CSS fade begins as narration starts
    }, delay);
    return () => clearTimeout(t);
  }, [text, id, isAutoSpeak, delay, speak]);

  // Safety: if narrator never fires for any reason, reveal after grace
  useEffect(() => {
    if (manual || !supported || muted) return;
    const t = setTimeout(() => setRevealed(true), 2500);
    return () => clearTimeout(t);
  }, [text, id, manual, supported, muted]);

  // Stop narration if we started it and this component unmounts
  useEffect(() => {
    return () => {
      if (startedRef.current && active) stop();
    };
  }, [active, stop]);

  const onPlayPauseClick = (e) => {
    e.stopPropagation();
    if (active && isSpeaking) {
      stop();
      return;
    }
    startedRef.current = true;
    speak(text, id);
  };

  const canPlay = supported && !muted;

  const body = (
    <Tag
      className={className}
      style={{
        ...style,
        opacity: revealed ? 1 : 0,
        transition: "opacity 1000ms ease-out",
        willChange: "opacity",
      }}
      data-narrated-id={id}
    >
      {text}
    </Tag>
  );

  if (!manual) return body;

  return (
    <div className="relative" data-narrated-block={id}>
      <div className="flex justify-end mb-2">
        <button
          type="button"
          data-testid={NARRATOR.playToggle(id)}
          onClick={onPlayPauseClick}
          disabled={!canPlay}
          aria-label={
            active && isSpeaking ? "Pause narration" : "Play narration"
          }
          title={active && isSpeaking ? "Pause narration" : "Play narration"}
          className="flex items-center justify-center h-9 w-9 rounded-full transition-all"
          style={{
            background: "rgba(15,15,15,0.55)",
            border: "1px solid rgba(212,175,55,0.35)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            color: "#D4AF37",
            opacity: canPlay ? 1 : 0.35,
            boxShadow:
              active && isSpeaking
                ? "0 0 14px rgba(212,175,55,0.35), inset 0 0 6px rgba(212,175,55,0.08)"
                : "none",
            cursor: canPlay ? "pointer" : "not-allowed",
          }}
        >
          {active && isSpeaking ? (
            <Pause size={14} strokeWidth={1.5} />
          ) : (
            <Play size={14} strokeWidth={1.5} />
          )}
        </button>
      </div>
      {body}
    </div>
  );
}
